import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { RefreshCw, TrendingUp, AlertCircle } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";

export default function Analytics() {
  const [funnel, setFunnel] = useState(null);
  const [emailPerf, setEmailPerf] = useState(null);
  const [apolloUsage, setApolloUsage] = useState(null);
  const [instantlySnap, setInstantlySnap] = useState(null);
  const [burndown, setBurndown] = useState(null);
  const [anglePerf, setAnglePerf] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [reactivation, setReactivation] = useState(null);
  const [lookalikes, setLookalikes] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [lookExpanding, setLookExpanding] = useState(false);

  const loadAll = async () => {
    try {
      const [f, e, a, i, b, ang, rev, react, lka] = await Promise.all([
        api.get("/analytics/funnel"),
        api.get("/analytics/email-perf"),
        api.get("/analytics/apollo-usage"),
        api.get("/analytics/instantly-snapshot"),
        api.get("/analytics/meeting-burndown"),
        api.get("/analytics/angle-performance"),
        api.get("/analytics/revenue-attribution"),
        api.get("/analytics/reactivation-queue"),
        api.get("/lookalike/profiles"),
      ]);
      setFunnel(f.data); setEmailPerf(e.data); setApolloUsage(a.data); setInstantlySnap(i.data); setBurndown(b.data);
      setAnglePerf(ang.data); setRevenue(rev.data); setReactivation(react.data); setLookalikes(lka.data || []);
    } catch (err) {
      toast.error("Failed to load analytics");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const runReactivation = async () => {
    setReactivating(true);
    try {
      const { data } = await api.post("/reactivation/run-now");
      if (data.ok) toast.success(`Reactivation drafted ${data.drafted} email(s) (eligible: ${data.eligible_found})`);
      else toast.error(data.reason || "Reactivation failed");
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reactivation failed");
    } finally {
      setReactivating(false);
    }
  };

  const expandLookalikes = async () => {
    setLookExpanding(true);
    try {
      const { data } = await api.post("/lookalike/expand-now", { max_per_run: 30 });
      if (data.ok) {
        const s = data.stats || {};
        toast.success(`Lookalike: ${s.imported || 0} imported · ${s.enrolled || 0} enrolled · ${s.out_of_region || 0} non-AU skipped`);
      } else {
        toast.error(data.reason || "Lookalike expansion failed");
      }
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lookalike failed");
    } finally {
      setLookExpanding(false);
    }
  };

  const toggleLookalike = async (profile_id, active) => {
    try {
      await api.patch(`/lookalike/profiles/${profile_id}`, { active });
      toast.success(active ? "Profile activated" : "Profile paused");
      await loadAll();
    } catch (err) {
      toast.error("Toggle failed");
    }
  };

  const syncInstantly = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/analytics/instantly/sync-now");
      if (data.ok) toast.success(`Synced ${data.synced} campaigns${data.errors ? ` · ${data.errors} errors` : ""}`);
      else toast.error(data.reason === "no_key" ? "Add Instantly key in Settings first" : (data.error || "Sync failed"));
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const syncBounces = async () => {
    if (!window.confirm("Pull every bounced lead from Instantly and HALT all future sends to them? Safe to re-run.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/instantly/sync-bounces");
      if (data.ok) {
        toast.success(`Bounce sync: ${data.marked_bounced_in_db} newly blocked · ${data.already_marked} already blocked · ${data.bounced_found_in_instantly} total in Instantly`);
      } else {
        toast.error(`Bounce sync failed: ${data.error || data.detail || "unknown"}`);
      }
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bounce sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (!funnel) return <Layout title="Analytics"><div className="text-sm text-muted-foreground">Loading…</div></Layout>;

  const it = instantlySnap?.totals || {};
  const sentI = it.emails_sent_count || 0;
  const openRate = sentI ? Math.round(((it.opens_count || 0) / sentI) * 100) : 0;
  const replyRate = sentI ? Math.round(((it.reply_count || 0) / sentI) * 100) : 0;
  const bounceRate = sentI ? Math.round(((it.bounce_count || 0) / sentI) * 100) : 0;

  return (
    <Layout title="Analytics" subtitle="Reporting" actions={
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={syncBounces} disabled={syncing} data-testid="analytics-sync-bounces" style={{ borderRadius: 2 }}>
          {syncing ? "Working…" : "Block bounced leads"}
        </Button>
        <Button variant="outline" onClick={syncInstantly} disabled={syncing} data-testid="analytics-sync-instantly" style={{ borderRadius: 2 }}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} strokeWidth={1.5} />
          {syncing ? "Syncing…" : "Sync Instantly now"}
        </Button>
      </div>
    }>
      {/* Meeting target burndown */}
      <section className="bg-card border border-border p-6 mb-6" style={{ borderRadius: 2 }} data-testid="burndown-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="overline mb-1">Weekly target</div>
            <div className="font-serif text-2xl tracking-tight" style={{ fontWeight: 450 }}>
              {burndown?.booked_this_week || 0} / {burndown?.target || 10} meetings booked this week
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {burndown?.on_track ? (
                <span className="text-[hsl(var(--success))]">✓ On track</span>
              ) : burndown?.remaining > 0 ? (
                <span className="text-[hsl(var(--warning))]">↗ Behind pace — {burndown.remaining} more needed</span>
              ) : (
                <span className="text-[hsl(var(--success))]">✓ Target hit</span>
              )}
            </div>
          </div>
          <BigStat label="Booked" value={burndown?.booked_this_week || 0} />
        </div>
        <div className="h-56" style={{ minHeight: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={burndown?.days || []}>
              <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid hsl(var(--border))" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="cumulative" name="Actual" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="target_cumulative" name="Target pace" stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funnel */}
        <section className="bg-card border border-border p-6" style={{ borderRadius: 2 }} data-testid="funnel-card">
          <div className="overline mb-1">Pipeline</div>
          <div className="font-serif text-xl tracking-tight mb-4" style={{ fontWeight: 450 }}>Cold → Client funnel</div>
          <div className="space-y-2">
            {funnel.stages.map((st, i) => (
              <div key={st.key} className="flex items-center gap-3" data-testid={`funnel-${st.key}`}>
                <div className="w-32 text-sm text-muted-foreground shrink-0">{st.label}</div>
                <div className="flex-1 bg-secondary h-7 relative" style={{ borderRadius: 2 }}>
                  <div
                    className="absolute inset-y-0 left-0 bg-primary"
                    style={{ width: `${Math.max(2, st.pct_of_total)}%`, borderRadius: 2 }}
                  />
                  <div className="relative px-2 h-full flex items-center justify-between text-xs font-mono">
                    <span className="text-foreground">{st.count}</span>
                    <span className="text-muted-foreground">{st.pct_of_total}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {funnel.stages[0].count === 0 && (
            <div className="text-xs text-muted-foreground mt-4">No leads yet — import via Lead Finder or seed.</div>
          )}
        </section>

        {/* Instantly performance */}
        <section className="bg-card border border-border p-6" style={{ borderRadius: 2 }} data-testid="instantly-card">
          <div className="flex items-center justify-between mb-1">
            <div className="overline">Cold-email outreach</div>
            <div className={`text-xs ${instantlySnap?.connected ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
              {instantlySnap?.connected ? "Instantly connected" : "Instantly not connected"}
            </div>
          </div>
          <div className="font-serif text-xl tracking-tight mb-4" style={{ fontWeight: 450 }}>Instantly campaigns</div>

          {!instantlySnap?.connected && (
            <div className="border border-border p-3 mb-4 flex items-start gap-2 text-sm" style={{ borderRadius: 2 }}>
              <AlertCircle className="h-4 w-4 text-[hsl(var(--warning))] mt-0.5" strokeWidth={1.5} />
              Add your Instantly API V2 key in Settings, then click <strong>Sync Instantly now</strong> above.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-4">
            <SmallStat label="Emails sent" value={sentI} />
            <SmallStat label="Open rate" value={`${openRate}%`} />
            <SmallStat label="Reply rate" value={`${replyRate}%`} />
            <SmallStat label="Bounce rate" value={`${bounceRate}%`} highlight={bounceRate > 5} />
          </div>

          <div className="text-xs text-muted-foreground mb-2">
            {instantlySnap?.last_sync_at
              ? `Last synced ${new Date(instantlySnap.last_sync_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}`
              : "Never synced — automatic daily sync at 03:00 UTC, or sync manually."}
          </div>

          {instantlySnap?.campaigns?.length > 0 && (
            <div className="border border-border" style={{ borderRadius: 2 }}>
              <table className="w-full text-xs">
                <thead className="bg-secondary/40 border-b border-border">
                  <tr>
                    <th className="text-left px-2 py-1.5 overline">Campaign</th>
                    <th className="text-right px-2 py-1.5 overline">Sent</th>
                    <th className="text-right px-2 py-1.5 overline">Opens</th>
                    <th className="text-right px-2 py-1.5 overline">Replies</th>
                    <th className="text-right px-2 py-1.5 overline">Bounce</th>
                  </tr>
                </thead>
                <tbody>
                  {instantlySnap.campaigns.map((c) => (
                    <tr key={c.campaign_id} className="border-b border-border last:border-0" data-testid={`instantly-row-${c.campaign_id}`}>
                      <td className="px-2 py-1.5 truncate max-w-[180px]">{c.campaign_name || c.campaign_id}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{c.emails_sent_count}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{c.opens_count}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{c.reply_count}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{c.bounce_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Email perf chart */}
        <section className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }} data-testid="email-perf-card">
          <div className="overline mb-1">Local email activity</div>
          <div className="font-serif text-xl tracking-tight mb-4" style={{ fontWeight: 450 }}>Sent / opened / replied — last 8 weeks</div>
          {(!emailPerf?.weeks || emailPerf.weeks.length === 0) ? (
            <div className="text-sm text-muted-foreground">No email activity yet.</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={emailPerf.weeks}>
                  <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid hsl(var(--border))" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="sent" name="Sent" fill="hsl(var(--primary))" />
                  <Bar dataKey="opened" name="Opened" fill="hsl(var(--accent-foreground))" />
                  <Bar dataKey="replied" name="Replied" fill="hsl(var(--success))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {emailPerf?.by_provider && emailPerf.by_provider.length > 0 && (
            <div className="mt-4 flex items-center gap-4 text-xs">
              <span className="overline">By provider:</span>
              {emailPerf.by_provider.map((p) => (
                <span key={p.provider} className="font-mono" data-testid={`provider-${p.provider}`}>
                  {p.provider}: <strong>{p.count}</strong>
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Apollo usage */}
        <section className="bg-card border border-border p-6 lg:col-span-2" style={{ borderRadius: 2 }} data-testid="apollo-card">
          <div className="flex items-center justify-between mb-1">
            <div className="overline">Lead sourcing</div>
            <div className={`text-xs ${apolloUsage?.connected ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
              {apolloUsage?.connected ? "Apollo connected" : "Apollo not connected"}
            </div>
          </div>
          <div className="font-serif text-xl tracking-tight mb-4" style={{ fontWeight: 450 }}>Apollo credit usage — last 30 days</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <SmallStat label="Searches" value={apolloUsage?.total_30d?.searches || 0} />
            <SmallStat label="Records returned" value={apolloUsage?.total_30d?.credits || 0} />
            <SmallStat label="Avg per search" value={apolloUsage?.total_30d?.searches ? Math.round((apolloUsage.total_30d.credits || 0) / apolloUsage.total_30d.searches) : 0} />
            <SmallStat label="Days with activity" value={apolloUsage?.by_day?.length || 0} />
          </div>
          {apolloUsage?.by_day?.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={apolloUsage.by_day}>
                  <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid hsl(var(--border))" }} />
                  <Bar dataKey="credits" name="Records" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No Apollo searches in the last 30 days. Run a search in <strong>Lead Finder</strong>.</div>
          )}
        </section>
      </div>

      {/* A/B/C ANGLE PERFORMANCE */}
      <section className="bg-card border border-border p-6 mt-6" style={{ borderRadius: 2 }} data-testid="angle-performance-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="overline mb-1">A/B/C Test · Angles</div>
            <div className="font-serif text-xl tracking-tight" style={{ fontWeight: 450 }}>Reply-rate per angle · Auto-winner</div>
            <div className="text-xs text-muted-foreground mt-1">
              {anglePerf?.winner ? (
                <span className="text-[hsl(var(--success))]">
                  ✓ Winner declared · <strong className="capitalize">{anglePerf.winner.angle}</strong> leads by {anglePerf.winner.lead_over_second_pp}pp · 70% of new Lusha leads will be routed here
                </span>
              ) : anglePerf?.eligible ? (
                <span className="text-muted-foreground">Eligible — no statistically meaningful winner yet (need ≥{anglePerf.threshold_reply_lead_pp}pp lead)</span>
              ) : (
                <span className="text-muted-foreground">Collecting data · need ≥{anglePerf?.threshold_sends || 200} sends per angle before declaring</span>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(anglePerf?.angles || []).map(a => {
            const isWinner = anglePerf?.winner?.angle === a.angle;
            return (
              <div key={a.angle} className="border p-4" style={{ borderRadius: 2, borderColor: isWinner ? "hsl(var(--success))" : "hsl(var(--border))", background: isWinner ? "hsl(var(--success) / 0.05)" : undefined }} data-testid={`angle-${a.angle}`}>
                <div className="flex items-center justify-between">
                  <div className="overline capitalize">{a.angle}</div>
                  {isWinner && <span className="text-[10px] tracking-wider uppercase font-mono text-[hsl(var(--success))]">★ Winner</span>}
                </div>
                <div className="font-serif text-3xl mt-2" style={{ fontWeight: 450 }}>{a.reply_rate}<span className="text-sm text-muted-foreground">%</span></div>
                <div className="text-xs text-muted-foreground mt-0.5">reply rate · {a.sent} sent</div>
                <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground space-y-1">
                  <div className="flex justify-between"><span>Leads</span><span className="font-mono text-foreground">{a.leads}</span></div>
                  <div className="flex justify-between"><span>Opened</span><span className="font-mono text-foreground">{a.opened} ({a.open_rate}%)</span></div>
                  <div className="flex justify-between"><span>Meetings</span><span className="font-mono text-foreground">{a.meetings} ({a.meeting_rate}%)</span></div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* REVENUE ATTRIBUTION */}
      <section className="bg-card border border-border p-6 mt-6" style={{ borderRadius: 2 }} data-testid="revenue-attribution-card">
        <div className="overline mb-1">Revenue attribution</div>
        <div className="font-serif text-xl tracking-tight mb-4" style={{ fontWeight: 450 }}>Won deals · pipeline value</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <SmallStat label="Won deals" value={revenue?.won?.deals || 0} />
          <SmallStat label="Won revenue" value={`$${((revenue?.won?.revenue || 0) / 1000).toFixed(1)}k`} />
          <SmallStat label="Avg deal" value={`$${((revenue?.won?.avg_deal || 0) / 1000).toFixed(1)}k`} />
          <SmallStat label="Pipeline value" value={`$${((revenue?.pipeline?.expected_revenue || 0) / 1000).toFixed(1)}k`} />
        </div>
        {(revenue?.by_angle?.length || revenue?.by_source?.length) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="overline mb-2">By angle</div>
              {(revenue?.by_angle || []).map(b => (
                <div key={b.key} className="flex items-center justify-between border-b border-border py-1.5 text-sm" data-testid={`rev-angle-${b.key}`}>
                  <span className="capitalize">{b.key}</span>
                  <span className="font-mono">{b.deals} deals · ${(b.revenue/1000).toFixed(1)}k</span>
                </div>
              ))}
            </div>
            <div>
              <div className="overline mb-2">By source</div>
              {(revenue?.by_source || []).map(b => (
                <div key={b.key} className="flex items-center justify-between border-b border-border py-1.5 text-sm" data-testid={`rev-source-${b.key}`}>
                  <span className="capitalize">{b.key}</span>
                  <span className="font-mono">{b.deals} deals · ${(b.revenue/1000).toFixed(1)}k</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">No won deals yet. Mark a lead as <strong>Client won</strong> in the Pipeline and enter the deal amount — it'll show up here.</div>
        )}
      </section>

      {/* REACTIVATION QUEUE */}
      <section className="bg-card border border-border p-6 mt-6" style={{ borderRadius: 2 }} data-testid="reactivation-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="overline mb-1">180-day rule</div>
            <div className="font-serif text-xl tracking-tight" style={{ fontWeight: 450 }}>Lost-deal re-engagement</div>
            <div className="text-xs text-muted-foreground mt-1">Daily cron auto-drafts a re-engagement email for cold leads (nurture/engaged/qualified) untouched for 180+ days.</div>
          </div>
          <Button variant="outline" onClick={runReactivation} disabled={reactivating} data-testid="reactivation-run-now" style={{ borderRadius: 2 }}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${reactivating ? "animate-spin" : ""}`} strokeWidth={1.5} />
            {reactivating ? "Running…" : "Run reactivation now"}
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <SmallStat label="Eligible today" value={reactivation?.eligible || 0} />
          <SmallStat label="Sent last 30d" value={reactivation?.sent_last_30d || 0} />
          <SmallStat label="Rule" value={`${reactivation?.rule_days || 180}d`} />
        </div>
      </section>

      {/* LOOKALIKE EXPANSION */}
      <section className="bg-card border border-border p-6 mt-6 mb-2" style={{ borderRadius: 2 }} data-testid="lookalike-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="overline mb-1">Lookalike expansion</div>
            <div className="font-serif text-xl tracking-tight" style={{ fontWeight: 450 }}>Clone your won deals</div>
            <div className="text-xs text-muted-foreground mt-1">
              Every <span className="text-foreground font-medium">client_won</span> lead auto-creates a lookalike profile (industry + employee band). Click <strong className="text-foreground">Pull lookalikes now</strong> to import 30 CFOs matching your won deals' profile.
            </div>
          </div>
          <Button variant="outline" onClick={expandLookalikes} disabled={lookExpanding || (lookalikes && lookalikes.length === 0)} data-testid="lookalike-expand-now" style={{ borderRadius: 2 }}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${lookExpanding ? "animate-spin" : ""}`} strokeWidth={1.5} />
            {lookExpanding ? "Pulling…" : "Pull lookalikes now"}
          </Button>
        </div>
        {lookalikes && lookalikes.length === 0 ? (
          <div className="text-sm text-muted-foreground">No lookalike profiles yet. Once you mark a lead as <strong className="text-foreground">Client won</strong> in the Pipeline, the engine snapshots its company profile here automatically.</div>
        ) : (
          <div className="space-y-2">
            {(lookalikes || []).map(p => (
              <div key={p.profile_id} className="flex items-center justify-between border border-border p-3 text-sm" style={{ borderRadius: 2 }} data-testid={`lookalike-profile-${p.profile_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-mono tracking-wider uppercase ${p.active ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`}>
                      {p.active ? "● ACTIVE" : "○ PAUSED"}
                    </span>
                    {p.deal_amount && <span className="text-[10px] font-mono text-muted-foreground">${Number(p.deal_amount).toLocaleString()}</span>}
                  </div>
                  <div className="font-medium truncate">{p.source_company}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {p.industry} · {p.employee_band ? `${p.employee_band.min}-${p.employee_band.max} emp` : "any size"} · {p.leads_sourced || 0} leads sourced
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => toggleLookalike(p.profile_id, !p.active)} data-testid={`toggle-lookalike-${p.profile_id}`} className="text-xs">
                  {p.active ? "Pause" : "Activate"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}

function SmallStat({ label, value, highlight }) {
  return (
    <div className="border border-border p-3" style={{ borderRadius: 2 }}>
      <div className="overline">{label}</div>
      <div className={`font-serif text-2xl mt-0.5 ${highlight ? "text-destructive" : ""}`} style={{ fontWeight: 450 }}>{value}</div>
    </div>
  );
}

function BigStat({ label, value }) {
  return (
    <div className="text-right">
      <div className="overline">{label}</div>
      <div className="font-serif text-5xl tracking-tight" style={{ fontWeight: 450 }}>{value}</div>
    </div>
  );
}
